/**
 * Admin Notifications Utility
 *
 * Generates system notifications for admin users based on
 * maintenance schedules, data refresh reminders, and other admin tasks.
 */

import industryBenchmarks from '../data/industryBenchmarks.json'
import regulatoryUpdates from '../data/regulatoryUpdates.json'

/**
 * Notification types
 */
export const NOTIFICATION_TYPES = {
  BENCHMARK_REFRESH: 'benchmark_refresh',
  BENCHMARK_OVERDUE: 'benchmark_overdue',
  STALE_ASSESSMENT: 'stale_assessment',
  THREAT_DATA_STALE: 'threat_data_stale',
  REGULATORY_UPDATE: 'regulatory_update',
  INFO: 'info',
  WARNING: 'warning'
}

/**
 * Get the priority level for a notification
 * @param {string} type - Notification type
 * @returns {number} Priority (1 = highest)
 */
function getPriority(type) {
  switch (type) {
    case NOTIFICATION_TYPES.BENCHMARK_OVERDUE:
      return 1
    case NOTIFICATION_TYPES.THREAT_DATA_STALE:
      return 2
    case NOTIFICATION_TYPES.REGULATORY_UPDATE:
      return 2
    case NOTIFICATION_TYPES.WARNING:
      return 3
    case NOTIFICATION_TYPES.STALE_ASSESSMENT:
      return 4
    case NOTIFICATION_TYPES.BENCHMARK_REFRESH:
      return 5
    case NOTIFICATION_TYPES.INFO:
      return 6
    default:
      return 7
  }
}

/**
 * Calculate days until a date
 * @param {string} dateString - ISO date string
 * @returns {number} Days until date (negative if past)
 */
function daysUntil(dateString) {
  const targetDate = new Date(dateString)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  targetDate.setHours(0, 0, 0, 0)
  const diffTime = targetDate - today
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * Format a date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Generate benchmark-related notifications
 * @returns {Array} Array of notification objects
 */
function getBenchmarkNotifications() {
  const notifications = []
  const metadata = industryBenchmarks.metadata

  if (!metadata?.nextReviewDate) {
    return notifications
  }

  const daysToReview = daysUntil(metadata.nextReviewDate)
  const lastUpdated = formatDate(metadata.lastUpdated)
  const reviewDate = formatDate(metadata.nextReviewDate)

  if (daysToReview < 0) {
    // Overdue
    notifications.push({
      id: 'benchmark-overdue',
      type: NOTIFICATION_TYPES.BENCHMARK_OVERDUE,
      title: 'Benchmark Data Overdue',
      message: `Industry benchmark data was due for refresh on ${reviewDate}. Last updated: ${lastUpdated}. Please review and update with current industry research.`,
      actionLabel: 'Review Sources',
      actionUrl: '/',
      priority: getPriority(NOTIFICATION_TYPES.BENCHMARK_OVERDUE),
      createdAt: new Date().toISOString(),
      metadata: {
        daysOverdue: Math.abs(daysToReview),
        lastUpdated: metadata.lastUpdated,
        nextReviewDate: metadata.nextReviewDate,
        version: metadata.version
      }
    })
  } else if (daysToReview <= 30) {
    // Due within 30 days
    notifications.push({
      id: 'benchmark-refresh-soon',
      type: NOTIFICATION_TYPES.BENCHMARK_REFRESH,
      title: 'Benchmark Refresh Due Soon',
      message: `Industry benchmark data refresh is due in ${daysToReview} day${daysToReview === 1 ? '' : 's'} (${reviewDate}). Last updated: ${lastUpdated}.`,
      actionLabel: 'View Benchmark Sources',
      actionUrl: '/',
      priority: getPriority(NOTIFICATION_TYPES.BENCHMARK_REFRESH),
      createdAt: new Date().toISOString(),
      metadata: {
        daysUntilDue: daysToReview,
        lastUpdated: metadata.lastUpdated,
        nextReviewDate: metadata.nextReviewDate,
        version: metadata.version
      }
    })
  } else if (daysToReview <= 60) {
    // Due within 60 days - informational
    notifications.push({
      id: 'benchmark-refresh-upcoming',
      type: NOTIFICATION_TYPES.INFO,
      title: 'Upcoming Benchmark Review',
      message: `Industry benchmark data review scheduled for ${reviewDate} (${daysToReview} days). Current version: ${metadata.version}.`,
      priority: getPriority(NOTIFICATION_TYPES.INFO),
      createdAt: new Date().toISOString(),
      metadata: {
        daysUntilDue: daysToReview,
        lastUpdated: metadata.lastUpdated,
        nextReviewDate: metadata.nextReviewDate,
        version: metadata.version
      }
    })
  }

  return notifications
}

const STALE_ASSESSMENT_DAYS = 14
const STALE_THREAT_DATA_DAYS = 90

/**
 * Generate notifications for in-progress assessments idle for 14+ days
 * @param {Array} clients - Array of client objects with assessments
 * @returns {Array} Array of notification objects
 */
function getStaleAssessmentNotifications(clients) {
  const notifications = []
  if (!clients?.length) return notifications

  for (const client of clients) {
    const assessments = client.assessments || []
    for (const assessment of assessments) {
      if (assessment.status !== 'in_progress') continue
      const referenceDate = assessment.createdAt
      if (!referenceDate) continue
      const daysSince = Math.floor((Date.now() - new Date(referenceDate).getTime()) / (1000 * 60 * 60 * 24))
      if (daysSince < STALE_ASSESSMENT_DAYS) continue

      notifications.push({
        id: `stale-assessment-${assessment.id}`,
        type: NOTIFICATION_TYPES.STALE_ASSESSMENT,
        title: 'Stale Assessment',
        message: `${client.name}'s assessment "${assessment.title || 'Untitled'}" has been in progress for ${daysSince} days without completion.`,
        actionLabel: 'View Client',
        actionUrl: `/clients/${client.id}`,
        priority: getPriority(NOTIFICATION_TYPES.STALE_ASSESSMENT),
        createdAt: new Date().toISOString(),
        metadata: {
          clientId: client.id,
          clientName: client.name,
          assessmentId: assessment.id,
          daysSince
        }
      })
    }
  }
  return notifications
}

/**
 * Generate notifications for completed assessments with threat data older than 90 days
 * @param {Array} clients - Array of client objects with assessments
 * @returns {Array} Array of notification objects
 */
function getStaleThreatDataNotifications(clients) {
  const notifications = []
  if (!clients?.length) return notifications

  for (const client of clients) {
    const assessments = client.assessments || []
    const completed = assessments
      .filter(a => a.status === 'completed' && a.completedAt)
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
    const latest = completed[0]
    if (!latest) continue

    // Check if assessment has threat context data
    const hasThreatData = latest.responses?.threatContext
    if (!hasThreatData) continue

    const daysSince = Math.floor((Date.now() - new Date(latest.completedAt).getTime()) / (1000 * 60 * 60 * 24))
    if (daysSince < STALE_THREAT_DATA_DAYS) continue

    notifications.push({
      id: `stale-threat-${client.id}`,
      type: NOTIFICATION_TYPES.THREAT_DATA_STALE,
      title: 'Threat Data Outdated',
      message: `${client.name}'s threat intelligence data is ${daysSince} days old. Review threat actor weights, attack vectors, and impact calibration.`,
      actionLabel: 'Review Threats',
      actionUrl: `/clients/${client.id}?tab=risk-intelligence`,
      priority: getPriority(NOTIFICATION_TYPES.THREAT_DATA_STALE),
      createdAt: new Date().toISOString(),
      metadata: {
        clientId: client.id,
        clientName: client.name,
        assessmentId: latest.id,
        daysSince
      }
    })
  }
  return notifications
}

/**
 * Generate notifications from AI regulatory monitoring findings.
 * Reads from regulatoryUpdates.json (updated weekly by CI).
 * @returns {Array} Array of notification objects
 */
function getRegulatoryUpdateNotifications() {
  const notifications = []
  if (!regulatoryUpdates?.findings?.length) return notifications

  for (const finding of regulatoryUpdates.findings) {
    if (finding.resolved) continue

    const severityPriority = finding.severity === 'high' ? 1
      : finding.severity === 'medium' ? 3 : 5

    notifications.push({
      id: finding.id,
      type: NOTIFICATION_TYPES.REGULATORY_UPDATE,
      title: finding.title,
      message: (finding.keyTakeaway || finding.summary || finding.detail || '').slice(0, 120) + ((finding.keyTakeaway || finding.summary || finding.detail || '').length > 120 ? '...' : ''),
      actionLabel: 'Review Data',
      actionUrl: `/regulatory-updates?expand=${finding.id}`,
      priority: severityPriority,
      createdAt: finding.detectedAt || new Date().toISOString(),
      metadata: {
        severity: finding.severity,
        category: finding.category,
        affectedProfile: finding.affectedProfile,
        citation: finding.citation,
        confidence: finding.confidence,
        isRegulatoryUpdate: true
      }
    })
  }
  return notifications
}

/**
 * Get all admin notifications
 * @param {Array} clients - Optional array of client objects for client-aware notifications
 * @returns {Array} Array of notification objects sorted by priority
 */
export function getAdminNotifications(clients) {
  const notifications = [
    ...getBenchmarkNotifications(),
    ...getStaleAssessmentNotifications(clients),
    ...getStaleThreatDataNotifications(clients),
    ...getRegulatoryUpdateNotifications()
  ]

  // Sort by priority (lower number = higher priority)
  return notifications.sort((a, b) => a.priority - b.priority)
}

/**
 * Get unread notification count
 * @param {Array} dismissedIds - Array of dismissed notification IDs from localStorage
 * @returns {number} Count of unread notifications
 */
export function getUnreadCount(dismissedIds = [], clients) {
  const notifications = getAdminNotifications(clients)
  return notifications.filter(n => !dismissedIds.includes(n.id)).length
}

/**
 * Get the color class for a notification type
 * @param {string} type - Notification type
 * @returns {Object} Color classes for icon and background
 */
export function getNotificationColors(type) {
  switch (type) {
    case NOTIFICATION_TYPES.BENCHMARK_OVERDUE:
      return {
        iconBg: 'bg-red-100 dark:bg-red-900/30',
        iconColor: 'text-red-600 dark:text-red-400',
        border: 'border-l-red-500'
      }
    case NOTIFICATION_TYPES.THREAT_DATA_STALE:
      return {
        iconBg: 'bg-amber-100 dark:bg-amber-900/30',
        iconColor: 'text-amber-600 dark:text-amber-400',
        border: 'border-l-amber-500'
      }
    case NOTIFICATION_TYPES.WARNING:
      return {
        iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
        iconColor: 'text-yellow-600 dark:text-yellow-400',
        border: 'border-l-yellow-500'
      }
    case NOTIFICATION_TYPES.STALE_ASSESSMENT:
      return {
        iconBg: 'bg-purple-100 dark:bg-purple-900/30',
        iconColor: 'text-purple-600 dark:text-purple-400',
        border: 'border-l-purple-500'
      }
    case NOTIFICATION_TYPES.REGULATORY_UPDATE:
      return {
        iconBg: 'bg-teal-100 dark:bg-teal-900/30',
        iconColor: 'text-teal-600 dark:text-teal-400',
        border: 'border-l-teal-500'
      }
    case NOTIFICATION_TYPES.BENCHMARK_REFRESH:
      return {
        iconBg: 'bg-orange-100 dark:bg-orange-900/30',
        iconColor: 'text-orange-600 dark:text-orange-400',
        border: 'border-l-orange-500'
      }
    case NOTIFICATION_TYPES.INFO:
    default:
      return {
        iconBg: 'bg-blue-100 dark:bg-blue-900/30',
        iconColor: 'text-blue-600 dark:text-blue-400',
        border: 'border-l-blue-500'
      }
  }
}

/**
 * Get the icon component for a notification type
 * @param {string} type - Notification type
 * @returns {string} Icon name/identifier
 */
export function getNotificationIcon(type) {
  switch (type) {
    case NOTIFICATION_TYPES.BENCHMARK_OVERDUE:
      return 'exclamation-circle'
    case NOTIFICATION_TYPES.THREAT_DATA_STALE:
      return 'shield-exclamation'
    case NOTIFICATION_TYPES.WARNING:
      return 'exclamation-triangle'
    case NOTIFICATION_TYPES.STALE_ASSESSMENT:
      return 'document-pause'
    case NOTIFICATION_TYPES.REGULATORY_UPDATE:
      return 'scale'
    case NOTIFICATION_TYPES.BENCHMARK_REFRESH:
      return 'clock'
    case NOTIFICATION_TYPES.INFO:
    default:
      return 'information-circle'
  }
}

export default {
  getAdminNotifications,
  getUnreadCount,
  getNotificationColors,
  getNotificationIcon,
  NOTIFICATION_TYPES
}
